import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import { safeRedirect } from '../utils/urlUtils';

export default function SkipCompletedPage() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const location = useLocation();

  // Use new centralized hooks
  const jwt = useJWT();
  const { serviceName } = useAppState();

  // Get service display name from JWT or fallback
  const serviceDisplayName =
    jwt.serviceName || serviceName || `SERVICE ${jwt.serviceId || ''}`;

  // Extract theme values safely
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const warningColor = (theme.palette?.warning as any)?.main || '#FD9726';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';
  const textPrimaryColor = (theme.palette?.text as any)?.primary || '#000000';
  const textSecondaryColor =
    (theme.palette?.text as any)?.secondary || '#586166';

  // Use JWT redirect capabilities instead of app state
  const canRedirectToService = jwt.canRedirectToService;
  const redirectURI = jwt.preferredRedirectURI;

  const feedbackParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const allowedKeys = ['skip_reason', 'skip_tsp_name'];
    const collected: Record<string, string> = {};

    allowedKeys.forEach((key) => {
      const value = params.get(key);
      if (value && value.trim()) {
        collected[key] = value.trim();
      }
    });

    return collected;
  }, [location.search]);

  // Track page view
  useEffect(() => {
    amplitude.track('Skip Completed Page View', {
      serviceName: serviceName,
    });
  }, [serviceName]);

  // Handle return to service (if redirect URI is available)
  const handleReturnToService = () => {
    amplitude.track('User Returned to Service from Skip Completed', {
      serviceName: serviceName,
      redirectURL: redirectURI,
    });

    if (redirectURI && redirectURI.startsWith('http')) {
      safeRedirect(redirectURI, undefined, feedbackParams);
    }
  };

  // Handle try again
  const handleTryAgain = () => {
    amplitude.track('User Chose Try Again from Skip Completed', {
      serviceName: serviceName,
    });

    navigate({
      pathname: '/',
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        minHeight: '100vh',
        py: { xs: 0, sm: 0 },
        px: { xs: 0, sm: 2 },
        pt: { xs: '16px', sm: 0 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: { xs: '100%', sm: '532px' },
          minWidth: { xs: '100vw', sm: '532px' },
          maxWidth: { xs: '100vw', sm: 'calc(100% - 32px)' },
          borderRadius: { xs: '0', sm: '4px' },
          position: 'relative',
          mb: { xs: 0, sm: 2 },
          mx: { xs: 0, sm: 'auto' },
          bgcolor: paperBgColor,
          p: { xs: '32px 16px', sm: '60px 50px' },
          textAlign: 'center',
        }}
      >
        {/* Info Icon */}
        <Box sx={{ mb: 3 }}>
          <InfoOutlinedIcon
            sx={{
              fontSize: { xs: 80, sm: 94 },
              color: warningColor,
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant='h4'
          sx={{
            mb: 2,
            fontWeight: 600,
            fontSize: { xs: '22px', sm: '28px' },
            lineHeight: { xs: '28px', sm: '36px' },
            color: textPrimaryColor,
            wordWrap: 'break-word',
            hyphens: 'auto',
          }}
        >
          Linking Telematics Skipped
        </Typography>

        {/* Description */}
        <Typography
          variant='body1'
          sx={{
            mb: 4,
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: { xs: '20px', sm: '24px' },
            color: textSecondaryColor,
            maxWidth: '400px',
            mx: 'auto',
          }}
        >
          You've chosen to skip the linking telematics process.
          {canRedirectToService && serviceDisplayName && (
            <> We've notified {serviceDisplayName} about your decision.</>
          )}
        </Typography>

        {/* Buttons */}
        <Stack spacing={1.25} sx={{ width: '100%' }}>
          {/* Primary action - Return to service or show close message */}
          {canRedirectToService ? (
            <Button
              variant='contained'
              onClick={handleReturnToService}
              sx={{
                height: { xs: '48px', sm: '40px' },
                borderRadius: '8px',
                textTransform: 'uppercase',
                fontWeight: 600,
                fontSize: { xs: '16px', sm: '14px' },
                letterSpacing: '1.25px',
                bgcolor: primaryColor,
                '&:hover': {
                  bgcolor: primaryColor,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              RETURN TO {serviceDisplayName}
            </Button>
          ) : (
            <Typography
              variant='body2'
              sx={{
                textAlign: 'center',
                fontStyle: 'italic',
                color: 'text.secondary',
                mt: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              You can safely close this tab or window.
            </Typography>
          )}

          {/* Secondary action - Try again */}
          <Button
            variant='text'
            onClick={handleTryAgain}
            sx={{
              height: { xs: '48px', sm: '40px' },
              borderRadius: '8px',
              textTransform: 'uppercase',
              fontWeight: 600,
              fontSize: { xs: '16px', sm: '14px' },
              letterSpacing: '1.25px',
              color: primaryColor,
              '&:hover': {
                bgcolor: `rgba(${parseInt(
                  primaryColor.slice(1, 3),
                  16
                )}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(
                  primaryColor.slice(5, 7),
                  16
                )}, 0.04)`,
              },
            }}
          >
            RETRY LINKING TELEMATICS
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
